import { useEffect, useState } from "react";
import { X, Share2, Trophy, Ticket, Gift, ListChecks } from "lucide-react";
import type { MatchRewardSummary } from "@/lib/pr25/postMatchSummary";
import { trackEvent } from "@/lib/audit";
import { storeRecap } from "@/lib/retention/postMatchRecap";
import { MatchTop3Surface } from "./MatchTop3Surface";
import { SmartSurveyDeck } from "@/components/polls/SmartSurveyDeck";
import { openShareSheet } from "@/components/viral/ShareSheetHost";

export function PostMatchSummaryModal() {
  const [summary, setSummary] = useState<MatchRewardSummary | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<MatchRewardSummary>;
      if (ce.detail) {
        setSummary(ce.detail);
        storeRecap(ce.detail);
      }
    };
    window.addEventListener("ss2026:post-match-summary", handler as EventListener);
    return () => window.removeEventListener("ss2026:post-match-summary", handler as EventListener);
  }, []);

  if (!summary) return null;

  const close = () => setSummary(null);
  const share = () => {
    void trackEvent("post_match_summary_shared", { match_id: summary.match_id });
    void trackEvent("post_match_share_top3_clicked", { match_id: summary.match_id });
    openShareSheet();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur">
      <div className="w-full max-w-md rounded-2xl border border-border bg-background p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black">¡Partido finalizado!</h3>
          <button onClick={close} aria-label="Cerrar" className="rounded-full p-1 hover:bg-accent"><X className="h-4 w-4" /></button>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">Estos son tus resultados</p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
          <Card icon={<Trophy className="h-3.5 w-3.5" />} label="Puntos" value={`+${summary.points_earned}`} />
          <Card icon={<ListChecks className="h-3.5 w-3.5" />} label="Encuestas" value={summary.surveys_answered} />
          <Card icon={<Trophy className="h-3.5 w-3.5" />} label="Predicciones OK" value={summary.predictions_correct} />
          <Card icon={<Ticket className="h-3.5 w-3.5" />} label="Cupones" value={summary.coupons_obtained} />
          <Card icon={<Gift className="h-3.5 w-3.5" />} label="Premios" value={summary.rewards_unlocked.length} />
          <Card icon={<Trophy className="h-3.5 w-3.5" />} label="Ranking" value={`#${summary.ranking_position}`} />
        </div>
        <div className="mt-3 rounded border border-border/40 p-2 text-[11px]">
          <div className="font-bold">Badges</div>
          <div className="text-muted-foreground">{summary.badges_earned.join(", ") || "—"}</div>
        </div>
        <div className="mt-3 text-[11px] text-muted-foreground">Saldo total: <b className="text-foreground">{summary.total_balance}</b></div>
        <div className="mt-3">
          <MatchTop3Surface />
        </div>
        <div className="mt-3 space-y-2">
          <SmartSurveyDeck
            categories={["fan_profile", "post_match_insight"]}
            window="post_match"
            label="Fan DNA"
            storageKey="vs:smart_survey:postmatch_fan_dna"
            minIntervalMs={60_000}
            matchId={summary.match_id}
          />
          <SmartSurveyDeck
            categories={["community"]}
            window="post_match"
            label="Comunidad"
            storageKey="vs:smart_survey:postmatch_community"
            minIntervalMs={60_000}
            matchId={summary.match_id}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => { window.dispatchEvent(new CustomEvent("ss2026:open-rewards")); close(); }} className="rounded bg-secondary px-3 py-1.5 text-[12px]">Ver premios</button>
          <button onClick={() => { window.dispatchEvent(new CustomEvent("ss2026:open-wallet")); close(); }} className="rounded bg-secondary px-3 py-1.5 text-[12px]">Ofertas guardadas</button>
          <button onClick={share} className="ml-auto flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-[12px] text-primary-foreground"><Share2 className="h-3 w-3" /> Compartir</button>
        </div>
      </div>
    </div>
  );
}

function Card({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="rounded border border-border/40 bg-background/40 p-2">
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">{icon}{label}</div>
      <div className="text-sm font-black tabular-nums">{value}</div>
    </div>
  );
}